import type { 
  AuthResponse, 
  LoginInput, 
  RegisterInput, 
  Client, 
  CreateClientInput,
  Program,
  Checkin,
  CreateCheckinInput,
  ApiError 
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

function getToken(): string | null {
  return localStorage.getItem('ptai_token');
}

function setToken(token: string): void {
  localStorage.setItem('ptai_token', token);
}

function removeToken(): void {
  localStorage.removeItem('ptai_token');
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  const data = await response.json();

  if (!response.ok) {
    const error = data as ApiError;
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return data as T;
}

// Auth API
export async function register(input: RegisterInput): Promise<AuthResponse> {
  const response = await fetchApi<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  setToken(response.token);
  return response;
}

export async function login(input: LoginInput): Promise<AuthResponse> {
  const response = await fetchApi<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  setToken(response.token);
  return response;
}

export function logout(): void {
  removeToken();
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

// Clients API
export async function getClients(): Promise<Client[]> {
  return fetchApi<Client[]>('/clients');
}

export async function createClient(input: CreateClientInput): Promise<Client> {
  return fetchApi<Client>('/clients', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteClient(id: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/clients/${id}`, {
    method: 'DELETE',
  });
}

// Programs API
export async function generateProgram(clientId: string): Promise<Program> {
  return fetchApi<Program>('/programs/generate', {
    method: 'POST',
    body: JSON.stringify({ clientId }),
  });
}

export async function getProgramsByClient(clientId: string): Promise<Program[]> {
  return fetchApi<Program[]>(`/programs/${clientId}`);
}

export async function getProgramById(id: string): Promise<Program> {
  return fetchApi<Program>(`/programs/detail/${id}`);
}

// Checkins API
export async function createCheckin(input: CreateCheckinInput): Promise<Checkin> {
  return fetchApi<Checkin>('/checkins', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getCheckinsByClient(clientId: string): Promise<Checkin[]> {
  return fetchApi<Checkin[]>(`/checkins/${clientId}`);
}
